import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { Bell, Check, User as UserIcon, Play, Ban, Pause, Search as SearchIcon, CheckCircle2, X, Trash, Inbox, Clock } from "lucide-react";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { cn } from "@/lib/utils";
import { formatDistanceToNow } from "date-fns";
import { es, enUS } from "date-fns/locale";
import { useTranslation } from "react-i18next";

const STATUS_ICON = {
  pending:     Clock,
  received:    Inbox,
  in_progress: Play,
  blocked:     Ban,
  postponed:   Pause,
  review:      SearchIcon,
  completed:   CheckCircle2,
  denied:      X,
  discarded:   Trash,
};

function initials(name = "") {
  return name.split(/\s+/).map((p) => p[0]).filter(Boolean).slice(0, 2).join("").toUpperCase() || "?";
}

export default function NotificationsBell() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { t, i18n } = useTranslation();
  const dfLocale = i18n.language?.startsWith("en") ? enUS : es;

  const { data } = useQuery({
    queryKey: ["notifications-count"],
    queryFn: async () => (await api.get("/notifications", { params: { limit: 15 } })).data,
    refetchInterval: 45_000,
    refetchOnWindowFocus: true,
  });

  const items = data?.notifications || [];
  const unread = data?.unread || 0;

  const markOne = useMutation({
    mutationFn: async (notif_id) => (await api.post(`/notifications/${notif_id}/read`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["notifications-count"] }),
  });
  const markAll = useMutation({
    mutationFn: async () => (await api.post("/notifications/read-all")).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["notifications-count"] }),
  });

  const openNotif = (n) => {
    if (!n.is_read) markOne.mutate(n.notif_id);
    if (n.link) navigate(n.link);
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          data-testid="notifications-bell"
          className="relative h-9 w-9 rounded-full grid place-items-center hover:bg-white/40 dark:hover:bg-white/10 transition"
          aria-label={t("notifications.aria", "Notificaciones")}
        >
          <Bell className="h-4 w-4" />
          {unread > 0 && (
            <span
              data-testid="notifications-unread-badge"
              className="absolute top-1 right-1 min-w-[16px] h-4 px-1 rounded-full bg-rose-500 text-white text-[9px] font-bold grid place-items-center leading-none animate-pulse"
            >
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-96 max-w-[92vw] p-0 glass-panel rounded-2xl overflow-hidden"
        data-testid="notifications-popover"
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-white/30 dark:border-white/10">
          <p className="font-display text-sm font-semibold">{t("notifications.title", "Notificaciones")}</p>
          {unread > 0 && (
            <button
              onClick={() => markAll.mutate()}
              data-testid="notifications-mark-all-read"
              className="text-[11px] text-blue-600 hover:underline"
            >
              {t("notifications.markAllRead", "Marcar todo como leído")}
            </button>
          )}
        </div>
        <div className="max-h-96 overflow-y-auto kanban-scroll">
          {items.length === 0 ? (
            <div className="p-6 text-center text-sm text-muted-foreground" data-testid="notifications-empty">
              {t("notifications.empty", "No tienes notificaciones aún.")}
            </div>
          ) : (
            <ul className="py-1">
              {items.map((n) => {
                const StatusI = STATUS_ICON[n.meta?.to] || null;
                return (
                  <li key={n.notif_id}>
                    <button
                      onClick={() => openNotif(n)}
                      data-testid={`notification-${n.notif_id}`}
                      className={cn(
                        "w-full text-left px-4 py-3 flex items-start gap-3 hover:bg-white/50 dark:hover:bg-white/5 transition",
                        !n.is_read && "bg-blue-500/5"
                      )}
                    >
                      {n.actor?.picture || n.actor?.name ? (
                        <Avatar className="h-8 w-8 flex-none">
                          <AvatarImage src={n.actor?.picture} alt={n.actor?.name} />
                          <AvatarFallback className="text-[10px] bg-blue-500/20">{initials(n.actor?.name || "?")}</AvatarFallback>
                        </Avatar>
                      ) : (
                        <div className="h-8 w-8 rounded-full bg-blue-500/15 grid place-items-center flex-none">
                          <UserIcon className="h-4 w-4 text-blue-600 dark:text-blue-300" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate flex items-center gap-1.5">
                          {n.title}
                          {StatusI && <StatusI className="h-3.5 w-3.5 text-muted-foreground" />}
                        </p>
                        <p className="text-xs text-muted-foreground line-clamp-2 mt-0.5">{n.body}</p>
                        <p className="text-[11px] text-muted-foreground mt-1">
                          {formatDistanceToNow(new Date(n.created_at), { addSuffix: true, locale: dfLocale })}
                        </p>
                      </div>
                      {!n.is_read && <span className="mt-2 h-2 w-2 rounded-full bg-blue-500 flex-none" />}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
